import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Rewrites absolute digital24.com links inside rich article HTML to
 * relative SPA paths and intercepts clicks so internal navigation
 * stays within the React Router (no full page reload).
 *
 * Also fixes legacy path slugs that no longer exist:
 *   /help-and-support            → /help
 *   /services/guaranteed-media-placements → /services/guaranteed-placements
 *   /why-digital24               → /about
 */
const LEGACY_PATH_MAP = {
  "/help-and-support": "/help",
  "/services/guaranteed-media-placements": "/services/guaranteed-placements",
  "/why-digital24": "/about",
};

const DOMAIN_MATCH = /^https?:\/\/(?:www\.)?digital24\.com/;

function rewriteHref(href) {
  if (!href) return href;
  // External or non-http links — leave alone
  if (!href.startsWith("http")) return href;
  if (!DOMAIN_MATCH.test(href)) return href;

  let path = href.replace(DOMAIN_MATCH, "") || "/";
  // Strip query/hash for legacy matching, preserve for final
  const queryIndex = path.indexOf("?");
  const hashIndex = path.indexOf("#");
  const endIdx = Math.min(
    queryIndex === -1 ? Infinity : queryIndex,
    hashIndex === -1 ? Infinity : hashIndex
  );
  const pathname = path.slice(0, endIdx === Infinity ? undefined : endIdx);
  const suffix = endIdx === Infinity ? "" : path.slice(endIdx);

  const fixed = LEGACY_PATH_MAP[pathname] || pathname;
  return fixed + suffix;
}

export default function RichContent({ html, className, contentRef }) {
  const navigate = useNavigate();

  useEffect(() => {
    const el = contentRef?.current;
    if (!el) return;

    // Rewrite all internal links
    const anchors = el.querySelectorAll("a[href]");
    anchors.forEach((a) => {
      const original = a.getAttribute("href");
      const rewritten = rewriteHref(original);
      if (rewritten !== original) {
        a.setAttribute("href", rewritten);
      }
      // Open external links in a new tab so readers don't lose their place
      if (rewritten.startsWith("http")) {
        a.setAttribute("target", "_blank");
        a.setAttribute("rel", "noopener noreferrer");
      }
    });

    const handleClick = (e) => {
      const anchor = e.target.closest("a[href]");
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href) return;
      // Only intercept internal paths
      if (href.startsWith("http") || href.startsWith("mailto:") || href.startsWith("tel:")) return;
      if (href.startsWith("#")) return; // anchor links handled by GuideToc

      e.preventDefault();
      navigate(href);
    };

    el.addEventListener("click", handleClick);
    return () => el.removeEventListener("click", handleClick);
  }, [html, contentRef, navigate]);

  return (
    <div
      ref={contentRef}
      className={className}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}